import React, { useMemo } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Building2, Users, UserCheck, UserCircle, Home, Settings,
  Pencil, Check, X
} from 'lucide-react';
import { CommunityConfigPanel } from './CommunityConfigPanel';
import { CommunityStoragePanel } from './CommunityStoragePanel';
import { MCMembersPanel } from './MCMembersPanel';

export function CreateCommunityModal({ open, onOpenChange, form, setForm, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-slate-900 border-gk-teal/30">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Building2 className="w-5 h-5 text-slate-400" />
            Create New Community
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label className="text-slate-300">Community Name *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Green Valley Society" className="bg-slate-800 border-gk-teal/30 text-white" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Address *</Label>
            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Full address" className="bg-slate-800 border-gk-teal/30 text-white" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Max Guards</Label>
            <Input type="number" value={form.max_guards} onChange={(e) => setForm({ ...form, max_guards: parseInt(e.target.value) || 3 })} min={1} max={10} className="bg-slate-800 border-gk-teal/30 text-white" />
          </div>
          <div className="pt-4 border-t border-gk-teal/30">
            <p className="text-sm font-medium text-slate-300 mb-3">Management User</p>
            <div className="space-y-3">
              <div className="space-y-2">
                <Label className="text-slate-300">Name *</Label>
                <Input value={form.management_name} onChange={(e) => setForm({ ...form, management_name: e.target.value })} placeholder="Manager name" className="bg-slate-800 border-gk-teal/30 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Email *</Label>
                <Input type="email" value={form.management_email} onChange={(e) => setForm({ ...form, management_email: e.target.value })} placeholder="manager@example.com" className="bg-slate-800 border-gk-teal/30 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Password *</Label>
                <Input type="password" value={form.management_password} onChange={(e) => setForm({ ...form, management_password: e.target.value })} placeholder="Set password" className="bg-slate-800 border-gk-teal/30 text-white" />
              </div>
            </div>
          </div>
          <div className="flex gap-2 pt-4">
            <Button onClick={onSubmit} className="flex-1 bg-gk-teal hover:bg-gk-teal-hover">Create Community</Button>
            <Button onClick={() => onOpenChange(false)} variant="outline" className="border-gk-teal/50 text-slate-300">Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CommunityDetailsModal({
  open, onOpenChange, community, users,
  editingName, setEditingName, editName, setEditName, onSaveName,
  editingAddress, setEditingAddress, editAddress, setEditAddress, onSaveAddress,
  editingMgmt, setEditingMgmt, editMgmtEmail, setEditMgmtEmail,
  editMgmtPhone, setEditMgmtPhone, editMgmtName, setEditMgmtName,
  onStartEditMgmt, onSaveMgmt, saving
}) {
  const mgmtUser = useMemo(() => users.find(u => u.role === 'management'), [users]);
  const otherUsers = useMemo(() => users.filter(u => u.role !== 'management'), [users]);

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); setEditingName(false); setEditingAddress(false); setEditingMgmt(false); }}>
      <DialogContent className="max-w-2xl bg-slate-900 border-gk-teal/30 max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Building2 className="w-5 h-5 text-slate-400" />
            {editingName ? (
              <div className="flex items-center gap-2 flex-1">
                <Input data-testid="edit-community-name-input" value={editName} onChange={(e) => setEditName(e.target.value)} className="bg-slate-800 border-gk-teal/30 text-white h-8" autoFocus onKeyPress={(e) => e.key === 'Enter' && onSaveName()} />
                <Button size="sm" onClick={onSaveName} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 h-8 w-8 p-0"><Check className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => setEditingName(false)} className="text-slate-400 h-8 w-8 p-0"><X className="w-4 h-4" /></Button>
              </div>
            ) : (
              <span className="flex items-center gap-2">
                {community?.name}
                <button data-testid="edit-community-name-button" onClick={() => { setEditName(community?.name || ''); setEditingName(true); }} className="text-gk-teal hover:text-gk-teal/80 transition-colors">
                  <Pencil className="w-4 h-4" />
                </button>
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-slate-400">Community details, configuration, and user management</DialogDescription>
        </DialogHeader>
        {community && (
          <div className="space-y-4 py-4">
            <div className="p-4 bg-slate-800/50 rounded-lg">
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm text-slate-300">Address</p>
                {!editingAddress && (
                  <button data-testid="edit-community-address-button" onClick={() => { setEditAddress(community?.address || ''); setEditingAddress(true); }} className="text-gk-teal hover:text-gk-teal/80 transition-colors">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              {editingAddress ? (
                <div className="flex items-center gap-2">
                  <Input data-testid="edit-community-address-input" value={editAddress} onChange={(e) => setEditAddress(e.target.value)} className="bg-slate-800 border-gk-teal/30 text-white h-8 flex-1" autoFocus onKeyPress={(e) => e.key === 'Enter' && onSaveAddress()} />
                  <Button size="sm" onClick={onSaveAddress} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 h-8 w-8 p-0"><Check className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingAddress(false)} className="text-slate-400 h-8 w-8 p-0"><X className="w-4 h-4" /></Button>
                </div>
              ) : (
                <p className="text-white">{community.address}</p>
              )}
              <div className="flex gap-4 mt-3">
                <div>
                  <p className="text-xs text-slate-400">Max Guards</p>
                  <p className="text-lg font-bold text-white">{community.max_guards}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Created</p>
                  <p className="text-sm text-white">{new Date(community.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            </div>

            {mgmtUser && (
              <div className="p-4 bg-blue-900/20 rounded-lg border border-blue-500/30">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-blue-300 flex items-center gap-1.5">
                    <Settings className="w-4 h-4" />
                    Management Account
                  </h4>
                  {!editingMgmt ? (
                    <Button data-testid="edit-management-button" size="sm" variant="outline" onClick={() => onStartEditMgmt(mgmtUser)} className="border-blue-500/40 text-blue-300 hover:bg-blue-600/20 h-7 text-xs">
                      <Pencil className="w-3 h-3 mr-1" />Edit
                    </Button>
                  ) : (
                    <div className="flex gap-1">
                      <Button size="sm" onClick={onSaveMgmt} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs">{saving ? 'Saving...' : 'Save'}</Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingMgmt(false)} className="text-slate-400 h-7 text-xs">Cancel</Button>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  {editingMgmt ? (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400 w-14">Name:</span>
                        <Input data-testid="edit-mgmt-name-input" type="text" value={editMgmtName} onChange={(e) => setEditMgmtName(e.target.value)} className="bg-slate-800 border-blue-500/30 text-white h-8 text-sm flex-1" />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400 w-14">Email:</span>
                        <Input data-testid="edit-mgmt-email-input" type="email" value={editMgmtEmail} onChange={(e) => setEditMgmtEmail(e.target.value)} className="bg-slate-800 border-blue-500/30 text-white h-8 text-sm flex-1" />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400 w-14">Phone:</span>
                        <Input data-testid="edit-mgmt-phone-input" type="tel" value={editMgmtPhone} onChange={(e) => setEditMgmtPhone(e.target.value)} placeholder="Enter phone number" className="bg-slate-800 border-blue-500/30 text-white h-8 text-sm flex-1" />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400 w-14">Name:</span>
                        <span className="text-white text-sm">{mgmtUser.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400 w-14">Email:</span>
                        <span className="text-white text-sm">{mgmtUser.email}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400 w-14">Phone:</span>
                        <span className="text-white text-sm">{mgmtUser.phone || 'Not set'}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            <CommunityStoragePanel communityId={community.id} />

            <CommunityConfigPanel communityId={community.id} communityName={community.name} />

            <MCMembersPanel communityId={community.id} />

            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-3">
                Other Users ({otherUsers.length})
              </h4>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {otherUsers.map((cUser) => (
                  <div key={cUser.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${cUser.role === 'guard' ? 'bg-amber-500' : 'bg-emerald-600'}`}>
                        {cUser.role === 'guard' ? <UserCheck className="w-4 h-4 text-white" /> : <Home className="w-4 h-4 text-white" />}
                      </div>
                      <div>
                        <p className="text-white font-medium">{cUser.name}</p>
                        <p className="text-xs text-slate-300">{cUser.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className={cUser.role === 'guard' ? 'bg-amber-500' : 'bg-emerald-600'}>{cUser.role}</Badge>
                      {cUser.villa_number && <p className="text-xs text-slate-400 mt-1">Villa: {cUser.villa_number}</p>}
                    </div>
                  </div>
                ))}
                {otherUsers.length === 0 && (
                  <p className="text-center text-slate-400 py-4 text-sm">No residents or guards yet</p>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function TileDetailModals({ showTileModal, setShowTileModal, communities, allUsers, getCommunityName }) {
  return (
    <>
      <Dialog open={showTileModal === 'communities'} onOpenChange={(open) => !open && setShowTileModal(null)}>
        <DialogContent className="max-w-2xl bg-slate-900 border-gk-teal/30 max-h-[80vh] overflow-y-auto" data-testid="sa-communities-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2"><Building2 className="w-5 h-5 text-gk-teal" /> All Communities ({communities.length})</DialogTitle>
            <DialogDescription className="text-slate-400">Community details with IDs</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {communities.map((c) => (
              <div key={c.id} className="p-4 bg-slate-800/50 rounded-lg border border-gk-teal/20">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-white">{c.name}</h4>
                  <Badge className="bg-gk-teal">{c.max_guards} guard max</Badge>
                </div>
                <p className="text-sm text-slate-300">{c.address}</p>
                <div className="mt-2 p-2 bg-gk-navy/50 rounded font-mono text-xs text-gk-teal break-all">
                  Community ID: {c.id}
                </div>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  <span className="text-xs text-slate-400">Residents: <span className="text-emerald-400 font-bold">{c.user_counts?.residents || 0}</span></span>
                  <span className="text-xs text-slate-400">Guards: <span className="text-amber-400 font-bold">{c.user_counts?.guards || 0}</span></span>
                  <span className="text-xs text-slate-400">Mgmt: <span className="text-blue-400 font-bold">{c.user_counts?.management || 0}</span></span>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showTileModal === 'residents'} onOpenChange={(open) => !open && setShowTileModal(null)}>
        <DialogContent className="max-w-2xl bg-slate-900 border-gk-teal/30 max-h-[80vh] overflow-y-auto" data-testid="sa-residents-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2"><Users className="w-5 h-5 text-emerald-500" /> All Residents ({allUsers.filter(u => u.role === 'resident').length})</DialogTitle>
            <DialogDescription className="text-slate-400">Residents across all communities</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            {allUsers.filter(u => u.role === 'resident').length === 0 ? (
              <p className="text-center text-slate-400 py-6">No residents found</p>
            ) : allUsers.filter(u => u.role === 'resident').map((u) => (
              <div key={u.id} className="p-3 bg-slate-800/50 rounded-lg flex items-center justify-between">
                <div>
                  <p className="font-medium text-white text-sm">{u.name}</p>
                  <p className="text-xs text-slate-400">{u.email}</p>
                  <p className="text-xs text-slate-500">{getCommunityName(u.community_id)} | Villa: {u.villa_number || 'N/A'}</p>
                </div>
                <div className="text-right">
                  <Badge className="bg-emerald-600 text-[10px]">{u.resident_type || 'resident'}</Badge>
                  <p className="text-[10px] text-slate-500 mt-1 font-mono">CID: {u.community_id?.slice(0, 8)}...</p>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showTileModal === 'guards'} onOpenChange={(open) => !open && setShowTileModal(null)}>
        <DialogContent className="max-w-2xl bg-slate-900 border-gk-teal/30 max-h-[80vh] overflow-y-auto" data-testid="sa-guards-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2"><UserCheck className="w-5 h-5 text-amber-500" /> All Guards ({allUsers.filter(u => u.role === 'guard').length})</DialogTitle>
            <DialogDescription className="text-slate-400">Guards across all communities</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            {allUsers.filter(u => u.role === 'guard').length === 0 ? (
              <p className="text-center text-slate-400 py-6">No guards found</p>
            ) : allUsers.filter(u => u.role === 'guard').map((u) => (
              <div key={u.id} className="p-3 bg-slate-800/50 rounded-lg flex items-center justify-between">
                <div>
                  <p className="font-medium text-white text-sm">{u.name}</p>
                  <p className="text-xs text-slate-400">{u.email}</p>
                  <p className="text-xs text-slate-500">{getCommunityName(u.community_id)}</p>
                </div>
                <div className="text-right">
                  <Badge className="bg-amber-500 text-[10px]">Guard</Badge>
                  <p className="text-[10px] text-slate-500 mt-1 font-mono">CID: {u.community_id?.slice(0, 8)}...</p>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showTileModal === 'management'} onOpenChange={(open) => !open && setShowTileModal(null)}>
        <DialogContent className="max-w-2xl bg-slate-900 border-gk-teal/30 max-h-[80vh] overflow-y-auto" data-testid="sa-management-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2"><UserCircle className="w-5 h-5 text-blue-500" /> Management Users ({allUsers.filter(u => u.role === 'management').length})</DialogTitle>
            <DialogDescription className="text-slate-400">Management accounts across communities</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            {allUsers.filter(u => u.role === 'management').length === 0 ? (
              <p className="text-center text-slate-400 py-6">No management users found</p>
            ) : allUsers.filter(u => u.role === 'management').map((u) => (
              <div key={u.id} className="p-3 bg-slate-800/50 rounded-lg flex items-center justify-between">
                <div>
                  <p className="font-medium text-white text-sm">{u.name}</p>
                  <p className="text-xs text-slate-400">{u.email}</p>
                  <p className="text-xs text-slate-500">{getCommunityName(u.community_id)}</p>
                </div>
                <div className="text-right">
                  <Badge className="bg-blue-600 text-[10px]">Management</Badge>
                  <p className="text-[10px] text-slate-500 mt-1 font-mono">CID: {u.community_id?.slice(0, 8)}...</p>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}


export function CommunityConfigModal({ open, onOpenChange, community }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-slate-900 border-gk-teal/30 max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Settings className="w-5 h-5 text-amber-400" />
            Configure: {community?.name}
          </DialogTitle>
          <DialogDescription className="text-slate-400">Toggle features and set limits for this community</DialogDescription>
        </DialogHeader>
        {community && (
          <div className="py-2">
            <CommunityConfigPanel communityId={community.id} communityName={community.name} />
            <MCMembersPanel communityId={community.id} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
